import { useState } from "react";
import { Outlet, useOutletContext, useParams, useNavigate } from "react-router";
import { Virtuoso } from "react-virtuoso";
import { Search, Grid } from "lucide-react";
import { cn } from "../../lib/utils";
import { useChats, href } from "../../util";
import type { Chat } from "../../lib/xmpp";

const AppItem = ({
	chat,
	isActive,
	onClick,
}: {
	chat: Chat;
	isActive: boolean;
	onClick: () => void;
}) => {
	const name = chat.displayName;
	const initials = name
		.split(" ")
		.map((w: string) => w[0])
		.join("")
		.slice(0, 2)
		.toUpperCase();

	return (
		<button
			onClick={onClick}
			className={cn(
				"flex items-center gap-3 px-4 py-3 w-full text-left transition-all hover:bg-white/5",
				isActive
					? "bg-white/10 border-l-2 border-primary"
					: "border-l-2 border-transparent",
			)}
		>
			{/* Avatar */}
			<div className="w-10 h-10 rounded-full bg-primary/50 flex items-center justify-center text-white text-sm font-medium shrink-0">
				{initials}
			</div>

			{/* Name */}
			<div className="flex-1 min-w-0">
				<span className="text-sm font-medium text-white/90 truncate block">{name}</span>
				<span className="text-xs text-white/40 truncate block">{chat.chatId}</span>
			</div>
		</button>
	);
};

export const Apps = () => {
	const ctx = useOutletContext<any>();
	const params = useParams();
	const navigate = useNavigate();
	const [search, setSearch] = useState("");

	const { client } = ctx;
	const accountId = params.accountId!;

	const allChats = useChats(client);
	const apps = allChats.filter((c) => c.isApp);

	const filtered = search
		? apps.filter((c) =>
				c.displayName.toLowerCase().includes(search.toLowerCase()),
			)
		: apps;

	const activeChatId = params.chatId;

	const onMobile = window.innerWidth < 768;
	const showSidebar = !onMobile || !activeChatId;

	const goToApp = (chatId: string) =>
		navigate(href("/accounts/:accountId/apps/:chatId", { accountId, chatId }), {
			replace: false,
		});

	return (
		<div className="flex h-full">
			{/* App list sidebar */}
			<aside
				className={cn(
					"relative flex flex-col glass-panel border-r border-white/8",
					"w-full md:w-72 lg:w-80 shrink-0",
					!showSidebar && "hidden md:flex",
				)}
			>
				{/* Header */}
				<div className="flex items-center justify-between px-4 py-3 border-b border-white/8 shrink-0">
					<span className="text-sm font-semibold text-white">Apps</span>
				</div>

				{/* Search bar (desktop) */}
				<div className="hidden md:block px-3 pb-3 shrink-0">
					<div className="relative">
						<Search
							size={16}
							className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
						/>
						<input
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder="Search apps..."
							className="w-full pl-9 pr-4 py-2 rounded-xl bg-white/8 border border-white/8 text-base text-white placeholder-white/40 focus:outline-none focus:border-white/20 transition-colors"
						/>
					</div>
				</div>

				{/* App list */}
				<div className="flex-1 overflow-hidden">
					{filtered.length === 0 ? (
						<div className="flex flex-col items-center justify-center h-full gap-3 text-white/40 px-6 text-center">
							<Grid size={40} strokeWidth={1} />
							<p className="text-sm">
								{search
									? "No apps match your search"
									: "No apps available"}
							</p>
						</div>
					) : (
						<Virtuoso
							style={{ height: "100%" }}
							data={filtered}
							itemContent={(_, chat) => (
								<AppItem
									key={chat.chatId}
									chat={chat}
									isActive={chat.chatId === activeChatId}
									onClick={() => goToApp(chat.chatId)}
								/>
							)}
						/>
					)}
				</div>

				{/* Mobile bottom search */}
				<div
					className={cn("md:hidden shrink-0 px-3 pt-2", activeChatId && "hidden")}
					style={{ paddingBottom: "calc(max(0.75rem, env(safe-area-inset-bottom)) + 0.25rem)" }}
				>
					<div className="flex items-center gap-2 px-3 py-2 rounded-2xl glass-liquid">
						<div className="relative flex-1">
							<Search
								size={15}
								className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none"
							/>
							<input
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								placeholder="Search..."
								className="w-full pl-9 pr-4 py-2 rounded-xl bg-white/6 border border-white/10 text-base text-white placeholder-white/35 focus:outline-none focus:border-white/25 focus:bg-white/10 transition-colors"
							/>
						</div>
					</div>
				</div>
			</aside>

			{/* App detail view */}
			<main className={cn("flex-1 overflow-hidden", !activeChatId && "hidden md:block")}>
				<Outlet context={{ ...ctx, accountId }} />
			</main>
		</div>
	);
};
